import { useCallback, useEffect, useMemo, useState } from "react";
import { Avatar, Button, Icon, Input, Select, Textarea, useToast } from "@astrum/ui";
import { stripMarkdown } from "@astrum/shared";
import { api, ApiError } from "../../lib/api.js";
import type { Channel, Message } from "../../lib/api.js";
import { iconForType } from "../../components/AttachmentList.js";
import { INLINE_IMAGE_TYPES } from "./types.js";
import type { PendingMessage, SearchablePerson } from "./types.js";

/* ─────────────────────────────── edit a message ─────────────────────────────── */

interface EditMessageDialogProps {
  message: PendingMessage;
  onClose: () => void;
  onSaved: (message: Message) => void;
}

export function EditMessageDialog({ message, onClose, onSaved }: EditMessageDialogProps) {
  const { toast } = useToast();
  const [value, setValue] = useState(message.body);
  const [saving, setSaving] = useState(false);

  const unchanged = value.trim() === message.body.trim();

  const save = useCallback(async () => {
    const body = value.trim();
    if (!body || unchanged) return;
    setSaving(true);
    try {
      const res = await api.patch<{ message: Message }>(`/messages/${message.id}`, { body });
      onSaved(res.message);
    } catch (error: unknown) {
      toast(error instanceof ApiError ? error.message : "Could not save that edit.", { tone: "danger" });
    } finally {
      setSaving(false);
    }
  }, [value, unchanged, message.id, onSaved, toast]);

  return (
    <div className="chat-edit">
      <Textarea
        aria-label="Message"
        value={value}
        maxRows={10}
        autoFocus
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          // Enter saves, matching the composer. Shift+Enter still breaks a line.
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            void save();
          }
          if (e.key === "Escape") onClose();
        }}
      />
      <div className="chat-edit__actions">
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button loading={saving} disabled={!value.trim() || unchanged} onClick={() => void save()}>
          Save
        </Button>
      </div>
    </div>
  );
}

/* ─────────────────────────────── raise work from chat ─────────────────────────────── */

interface RaiseTaskProps {
  projectId: string | null;
  /** Present when raised from a specific message, absent from the composer. */
  sourceMessage?: PendingMessage;
  onCreated: (task: NonNullable<Message["linkedTask"]>, sourceMessageId?: string) => void;
  onClose: () => void;
}

/**
 * Turns a conversation into work.
 *
 * The whole point of the fusion: a decision reached in chat becomes a task
 * without anyone re-typing it, and the message keeps a live card showing what
 * happened to it. `sourceMessageId` is what makes that link, and it has been in
 * the schema since the first migration.
 */
export function RaiseTaskDialog({ projectId, sourceMessage, onCreated, onClose }: RaiseTaskProps) {
  const { toast } = useToast();
  // Seeded from the message, because the message is almost always a usable
  // title — but editable, because "can someone check this?" is not a task name.
  const [title, setTitle] = useState(sourceMessage ? stripMarkdown(sourceMessage.body).slice(0, 200) : "");
  const [priority, setPriority] = useState("normal");
  const [dueAt, setDueAt] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = useCallback(() => {
    const trimmed = title.trim();
    if (!trimmed) return;

    setSaving(true);
    api
      .post<{ task: { id: string; title: string; status: string; priority: string; assigneeName: string | null; dueAt: string | null } }>(
        "/tasks",
        {
          title: trimmed,
          projectId,
          priority,
          // Midnight UTC: a calendar date carries no timezone and must not
          // acquire one in transit.
          dueAt: dueAt ? `${dueAt}T00:00:00.000Z` : undefined,
          sourceMessageId: sourceMessage?.id,
        },
      )
      .then((res) => {
        onCreated(
          {
            id: res.task.id,
            title: res.task.title,
            status: res.task.status,
            priority: res.task.priority,
            assigneeName: res.task.assigneeName,
            dueAt: res.task.dueAt,
          },
          sourceMessage?.id,
        );
        toast("Task created.");
        onClose();
      })
      .catch((error: unknown) => {
        toast(error instanceof ApiError ? error.message : "Could not create that task.", { tone: "danger" });
      })
      .finally(() => setSaving(false));
  }, [title, projectId, priority, dueAt, sourceMessage, onCreated, onClose, toast]);

  return (
    <form
      className="chat-raise"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      {sourceMessage && (
        <blockquote className="chat-raise__quote">
          <span className="chat-raise__quote-author">{sourceMessage.authorName}</span>
          {stripMarkdown(sourceMessage.body)}
        </blockquote>
      )}

      <Input
        label="Task"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        autoFocus
        required
      />

      <div className="chat-raise__row">
        <Select label="Priority" value={priority} onChange={(e) => setPriority(e.target.value)}>
          <option value="low">Low</option>
          <option value="normal">Normal</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
          <option value="urgent">Urgent</option>
        </Select>
        <Input label="Due date" type="date" value={dueAt} onChange={(e) => setDueAt(e.target.value)} />
      </div>

      {!projectId && (
        <p className="chat-raise__note">
          This channel is not linked to a project, so the task will not belong to one. You can move it later.
        </p>
      )}

      <div className="chat-raise__actions">
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" loading={saving} disabled={!title.trim()}>
          Create task
        </Button>
      </div>
    </form>
  );
}

/* ──────────────────────────────────── forward a file ──────────────────────────────────── */

/**
 * "Forward to…" — resends an already-uploaded attachment into a different
 * channel or DM without asking the sender to find and re-select the file.
 *
 * Lists the channels already joined, plus anyone in the directory search
 * has no thread with yet — mirroring the sidebar's own combined channel and
 * people search, since "who can I forward this to" is the same question as
 * "who can I start talking to" once existing threads are on the list too.
 */
export function ForwardDialog({
  channels,
  message,
  onClose,
  onForward,
}: {
  channels: Channel[];
  message: PendingMessage;
  onClose: () => void;
  onForward: (target: { channelId?: string; userId?: string }, note: string) => Promise<void>;
}) {
  const { toast } = useToast();
  const [query, setQuery] = useState("");
  const [people, setPeople] = useState<SearchablePerson[]>([]);
  const [sendingTo, setSendingTo] = useState<string | null>(null);
  const [note, setNote] = useState("");

  const trimmed = query.trim();
  const attachment = message.attachments?.[0];

  useEffect(() => {
    if (!trimmed || people.length > 0) return;
    let cancelled = false;
    api
      .get<{ people: SearchablePerson[] }>("/people")
      .then((res) => !cancelled && setPeople(res.people))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [trimmed, people.length]);

  const filteredChannels = useMemo(() => {
    const q = trimmed.toLowerCase();
    if (!q) return channels;
    return channels.filter((c) => c.name.toLowerCase().includes(q));
  }, [channels, trimmed]);

  // Anyone with no existing DM thread, matching the search — forwarding to
  // them opens (or creates) the thread the same way starting a new
  // conversation from the sidebar does.
  const strangers = useMemo(() => {
    const q = trimmed.toLowerCase();
    if (!q) return [];
    const talkingTo = new Set(
      channels.filter((c) => c.kind === "direct").map((c) => c.id),
    );
    return people.filter(
      (p) =>
        !talkingTo.has(p.id) &&
        [p.displayName, p.jobTitle, p.department].some((f) => f?.toLowerCase().includes(q)),
    );
  }, [people, channels, trimmed]);

  const send = useCallback(
    (key: string, target: { channelId?: string; userId?: string }) => {
      setSendingTo(key);
      onForward(target, note.trim())
        .then(onClose)
        .catch((error: unknown) => {
          toast(error instanceof ApiError ? error.message : "Could not forward that message.", { tone: "danger" });
        })
        .finally(() => setSendingTo(null));
    },
    [onForward, onClose, note, toast],
  );

  return (
    <div className="chat-forward">
      <p className="chat-forward__subject">
        {attachment ? (
          <Icon name={INLINE_IMAGE_TYPES.has(attachment.contentType) ? "attach" : iconForType(attachment.contentType)} size={14} />
        ) : (
          <Icon name="forward" size={14} />
        )}
        {message.authorName}: {stripMarkdown(message.body).slice(0, 120) || attachment?.fileName || "Message"}
      </p>
      <Textarea
        placeholder="Add a note (optional)"
        value={note}
        maxLength={2000}
        rows={2}
        onChange={(e) => setNote(e.target.value)}
      />
      <Input
        placeholder="Search channels and people"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        autoFocus
      />
      <div className="chat-forward__list">
        {filteredChannels.length === 0 && strangers.length === 0 ? (
          <p className="chat-add-members__empty">Nobody matches.</p>
        ) : (
          <>
            {filteredChannels.map((channel) => (
              <button
                key={channel.id}
                type="button"
                className="chat-forward__row"
                disabled={sendingTo !== null}
                onClick={() => send(channel.id, { channelId: channel.id })}
              >
                <Avatar name={channel.name} size={26} />
                <span className="chat-add-members__name">
                  <strong>{channel.name}</strong>
                  <span>{channel.kind === "direct" ? "Direct message" : "Channel"}</span>
                </span>
                {sendingTo === channel.id && <Icon name="spinner" size={14} />}
              </button>
            ))}
            {strangers.map((person) => (
              <button
                key={person.id}
                type="button"
                className="chat-forward__row"
                disabled={sendingTo !== null}
                onClick={() => send(person.id, { userId: person.id })}
              >
                <Avatar name={person.displayName} src={person.avatarUrl ?? undefined} size={26} />
                <span className="chat-add-members__name">
                  <strong>{person.displayName}</strong>
                  <span>{person.jobTitle ?? person.department ?? ""}</span>
                </span>
                {sendingTo === person.id && <Icon name="spinner" size={14} />}
              </button>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
